import React, { useState } from 'react';
import { NotificationBell } from './NotificationBell';
import { UserAvatarMenu } from './UserAvatarMenu';
import { SupportButton } from './SupportButton';
import { ProgressGridIcon } from './ProgressGridIcon';
import { ProgressGridModal } from './ProgressGridModal';
import { RoleSwitcher } from './RoleSwitcher';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import { isClientRoleName } from '../utils/clientRoles';
import type { WorkspaceId } from '../utils/workspaceRouter';
import type { TenantContext } from '../utils/workspaceRouter';

interface TopNavigationProps {
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
    avatarUrl?: string;
    firmId?: string;
    firmName?: string;
    isDemo?: boolean;
    roles?: string[];
    activeRole?: string;
  };
  companyId: string;
  companyName?: string;
  accessToken?: string;
  onLogout: () => void;
  children?: React.ReactNode;
  viewingClientId?: string;
  viewingClientName?: string;
  // Workspace switcher props (optional — only passed when workspace routing is active)
  activeWorkspace?: WorkspaceId;
  onWorkspaceChange?: (ws: WorkspaceId) => void;
  engagements?: any[];
  /** Override auto-detected tenant context for workspace switcher */
  tenantContext?: TenantContext;
  /** Optional SPA-style navigate callback. Falls back to window.location when omitted. */
  onNavigate?: (url: string) => void;
  /** Hide the personal to-do dropdown (the client home lists the client's work itself). */
  /** No longer used: to-dos live in the consultant's Queue (ledger L22). Kept so callers need not change. */
  hideTodos?: boolean;
}

export function TopNavigation({ 
  user, 
  companyId, 
  companyName, 
  accessToken, 
  onLogout, 
  children,
  viewingClientId,
  viewingClientName,
  activeWorkspace,
  onWorkspaceChange,
  engagements,
  tenantContext,
  onNavigate,
  hideTodos = false,
}: TopNavigationProps) {
  const [gridModalOpen, setGridModalOpen] = useState(false);
  const [selectedClientForGrid, setSelectedClientForGrid] = useState<string | null>(null);

  // SPA navigation for deep-links (avoids full page reload)
  // Uses the provided callback, or falls back to window navigation
  // when the component is rendered outside a Router context.
  const handleNavigate = onNavigate || ((url: string) => { window.location.href = url; });

  // Debug: Log user data to verify role switcher visibility
  console.log('[TopNavigation] Role switcher check:', {
    isDemo: user.isDemo,
    role: user.role,
    shouldShowRoleSwitcher: !!user.isDemo,
  });

  console.log('[ROLE_VIEW] TopNavigation current displayed role:', user.activeRole || user.role);

  // Debug: Log user data to verify Support button conditions
  console.log('[TopNavigation] User data for Support button:', {
    firmId: user.firmId,
    firmName: user.firmName,
    role: user.role,
    shouldShowSupport: ((user.firmId && user.firmName && (user.role === 'admin' || user.role === 'consultant' || user.role === 'cfAdmin' || user.role === 'consulting_firm_admin')) ||
      (user.role === 'systemAdmin' || user.role === 'system_admin'))
  });

  // Determine if the discovery grid should be shown
  const shouldShowProgressGrid = () => {
    // Never show for System Admins
    if (user.role === 'systemAdmin' || user.role === 'system_admin') {
      return false;
    }

    // Always show for clients, whatever their client role (company admins and
    // project leads used to be left out and had no way to reach their grid).
    if (isClientRoleName(user.role)) {
      return true;
    }

    // For consultants, show only when viewing a specific client
    if (
      user.role === 'consultant' || 
      user.role === 'admin' || 
      user.role === 'cf-admin' || 
      user.role === 'cfAdmin'
    ) {
      return !!viewingClientId;
    }

    return false;
  };

  const handleGridIconClick = () => {
    // For clients, open directly
    if (isClientRoleName(user.role)) {
      setGridModalOpen(true);
      return;
    }

    // For consultants viewing a client, open that client's grid
    if (viewingClientId) {
      setSelectedClientForGrid(viewingClientId);
      setGridModalOpen(true);
    }
  };

  return (
    <>
      <div className="border-b bg-background sticky top-0 z-50">
        <div className="flex h-16 items-center px-4 sm:px-6 gap-2 sm:gap-4">
          {/* Left side - Can include breadcrumbs or title */}
          <div className="flex-1 min-w-0">{children}</div>

          {/* Right side - Discovery grid, Support, Notifications and User Menu */}
          <div className="flex items-center gap-1 sm:gap-4 shrink-0">
            {/* Workspace Switcher — shown when workspace routing is active */}
            {activeWorkspace && onWorkspaceChange && (
              <WorkspaceSwitcher
                currentUser={user}
                engagements={engagements}
                activeWorkspace={activeWorkspace}
                onWorkspaceChange={onWorkspaceChange}
                tenantContext={tenantContext}
              />
            )}

            {/* Discovery grid icon */}
            {shouldShowProgressGrid() && accessToken && (
              <ProgressGridIcon
                companyId={viewingClientId || companyId}
                accessToken={accessToken}
                onClick={handleGridIconClick}
              />
            )}

            {/* Platform Support - For consultants, firm admins, and system admins */}
            {((user.firmId && user.firmName && (user.role === 'admin' || user.role === 'consultant' || user.role === 'cfAdmin' || user.role === 'consulting_firm_admin')) ||
              (user.role === 'systemAdmin' || user.role === 'system_admin')) && (
              <SupportButton
                firmId={user.firmId || 'system-admin'}
                firmName={user.firmName || 'PathKit Platform'}
                userId={user.id}
                userName={user.name}
                userEmail={user.email}
                variant="ghost"
                size="default"
                showLabel={false}
              />
            )}
            
            {/* Role Switcher - Shows for any user with multiple roles */}
            <RoleSwitcher
              currentUser={user}
              projectId={projectId}
              accessToken={accessToken || publicAnonKey}
            />
            
            <NotificationBell
              userId={user.id}
              userName={user.name}
              userEmail={user.email}
              userRole={user.role}
              companyId={companyId}
              projectId={projectId}
              publicAnonKey={publicAnonKey}
              accessToken={accessToken}
              onNavigate={handleNavigate}
            />
            
            <UserAvatarMenu
              user={user}
              companyId={companyId}
              companyName={companyName}
              accessToken={accessToken}
              projectId={projectId}
              publicAnonKey={publicAnonKey}
              onLogout={onLogout}
            />
          </div>
        </div>
      </div>

      {/* Discovery grid modal */}
      {gridModalOpen && (
        <ProgressGridModal
          isOpen={gridModalOpen}
          onClose={() => {
            setGridModalOpen(false);
            setSelectedClientForGrid(null);
          }}
          companyId={viewingClientId || companyId}
          companyName={viewingClientName || companyName}
          accessToken={accessToken || ''}
          currentUser={user}
        />
      )}
    </>
  );
}